import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, EyeOff, WifiOff, X } from "lucide-react";
import { anchorRef, seatAnchor } from "../fxAnchors";
import { ACTIONS, ROLES } from "../gameData";
import { useHoldReveal } from "../useHoldReveal";
import type { GameState, Player } from "../types";

interface Props {
  players: Player[];
  meId: string;
  activePlayerId: string | null;
  targeting: boolean;
  onTarget: (playerId: string) => void;
  /**
   * Only ever read to drop a pinned peek — the rail draws nothing from it.
   * Optional so the rail still renders without it, but leaving it out costs the
   * fix in the effect below: phase is the one signal that catches a response or
   * claim window opening, a pendingLoss and an exchange, because the engine sets
   * it in the same breath as each of them.
   */
  phase?: GameState["phase"];
  /**
   * True while `.court-center` is holding a PANEL — a response window, a
   * callout, a resolution line — as opposed to the idle readout or the
   * targeting stage. GameScreen derives it from the same conditions it picks
   * that branch with; `phase` cannot stand in for it, because a response phase
   * whose window is missing renders no panel at all.
   *
   * The rail does not decide whether the peek is visible — the stylesheet does,
   * and it hides the full one outright in this state. What the rail owes is a
   * SHORTER peek to place: header and lost-card tiles, no recent moves, no
   * footer. Optional so the rail still renders without it, at the cost of going
   * back to advertising a hold that mounts an invisible panel.
   */
  courtPanel?: boolean;
}

/*
 * Coins as a gauge rather than a number. In VEIL a coin pile is really a
 * countdown to violence: 7 buys a Coup, and at 10 a Coup is compulsory. Twelve
 * is the true ceiling — you can only ever start a turn on 9 (10 forces the
 * Coup) and the biggest single gain is Duke's +3.
 *
 * The arc deliberately stops short of the top. That 105 degree notch is where
 * the NOW / NEXT chip sits, which used to collide with the avatar; the gap
 * turns an old overlap into the shape's negative space.
 */
const COIN_MAX = 12;
const COUP_COST = 7;
const COUP_FORCED = 10;

const RING_R = 29;
const RING_C = 36;
const GAP_DEG = 105;
const SWEEP_DEG = 360 - GAP_DEG;
const SLOT_DEG = SWEEP_DEG / COIN_MAX;
const TICK_DEG = SLOT_DEG * 0.73;
const START_DEG = -90 + GAP_DEG / 2;

function tickPath(index: number) {
  const from = START_DEG + index * SLOT_DEG;
  const to = from + TICK_DEG;
  const point = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return `${(RING_C + RING_R * Math.cos(rad)).toFixed(2)} ${(RING_C + RING_R * Math.sin(rad)).toFixed(2)}`;
  };
  return `M ${point(from)} A ${RING_R} ${RING_R} 0 0 1 ${point(to)}`;
}

const TICKS = Array.from({ length: COIN_MAX }, (_, index) => tickPath(index));

function CoinRing({ coins }: { coins: number }) {
  const filled = Math.max(0, Math.min(COIN_MAX, coins));
  const level = filled >= COUP_FORCED ? "forced" : filled >= COUP_COST ? "armed" : "calm";
  return (
    <svg
      className={`coin-ring coin-ring--${level}`}
      viewBox="0 0 72 72"
      aria-hidden="true"
      focusable="false"
    >
      {TICKS.map((d, index) => (
        <path
          key={d}
          d={d}
          className={index < filled ? "coin-ring__tick coin-ring__tick--on" : "coin-ring__tick"}
        />
      ))}
    </svg>
  );
}

export function PlayerRail({
  players,
  meId,
  activePlayerId,
  targeting,
  onTarget,
  phase,
  courtPanel = false
}: Props) {
  // Targeting is checked first because it wins the court centre outright, and
  // `.target-stage` is the one panel the peek is NOT hidden under — choosing a
  // seat is exactly when "what has this rival already lost" is worth a hold, and
  // that stage carries no controls and no clock. GameScreen already accounts for
  // it; this is the belt to that brace, and it keeps the rail honest if the prop
  // is ever wired from somewhere that does not.
  const compact = courtPanel && !targeting;
  const others = players.filter((player) => player.id !== meId);
  const alivePlayers = players.filter((player) => player.alive);
  const activeTurnIndex = alivePlayers.findIndex((player) => player.id === activePlayerId);
  // 450ms, cancelled by pointer drift, and the "that was a hold" flag is cleared
  // synchronously by the click handler — so a 300ms press still selects a target.
  // The drag switch reads the seat id straight off the pill under the finger, so
  // sliding along the rail walks the peek from rival to rival in one gesture.
  const hold = useHoldReveal<string>({
    dragSwitch: { selector: "[data-seat-id]", value: (element) => element.dataset.seatId ?? null }
  });
  const peekPlayer = others.find((player) => player.id === hold.active) ?? null;
  // A pin survives the finger lifting, which is the only state that may claim
  // pointer events, a backdrop and a close button. A plain hold stays inert.
  const pinned = peekPlayer !== null && hold.pinned !== null;
  const pinnedId = hold.pinned;
  const { unpin } = hold;
  // A pinned peek is the only thing this rail puts BETWEEN the table and a
  // thumb, and it outlives the press that made it — so one left over from the
  // last beat eats the first tap of the next, and the taps that matter most
  // here are Pass and Doubt inside a five-second window. Anything that changes
  // what the table is asking for therefore drops the pin: `phase` covers every
  // interruption the server can raise (a response or claim window opening, a
  // pendingLoss, an exchange), `targeting` the one the client raises alone.
  // A HELD peek needs none of this — the finger lifting already closes it, and
  // dropping it here would kill a drag along the rail mid-gesture.
  useEffect(() => {
    unpin();
  }, [phase, targeting, unpin]);
  // The pinned seat itself going is the same trap wearing a different hat: a
  // panel about somebody who is no longer playing, over a backdrop that still
  // swallows taps. Held separately from the effect above because a seat can die
  // without the phase moving on — the loss that killed them may not be the last.
  const pinnedSeatGone =
    pinnedId !== null && !others.some((player) => player.id === pinnedId && player.alive);
  useEffect(() => {
    if (pinnedSeatGone) unpin();
  }, [pinnedSeatGone, unpin]);
  // Escape is the dismissal that survives whatever the backdrop is scoped to,
  // and the only one a keyboard has: the pin is opened by Enter or Space on a
  // pill and the close button is several tab stops away behind the panel.
  useEffect(() => {
    if (pinnedId === null) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") unpin();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [pinnedId, unpin]);
  const peekLostCards = peekPlayer?.influence.filter((card) => card.revealed && card.role) ?? [];
  // Per-person memory, newest first. VEIL keeps no table-wide log, but opening a
  // seat is meant to recall what THAT player has been claiming — that is reading
  // a person, which is the game, not reading a ledger.
  const peekPlays = peekPlayer ? [...(peekPlayer.recentPlays ?? [])].reverse() : [];

  return (
    <>
      <div
        className={[
          "player-rail",
          `player-rail--count-${others.length}`,
          targeting ? "player-rail--targeting" : ""
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={targeting ? "Choose a rival" : "Other players"}
      >
        {others.map((player, index) => {
          const lostCount = player.influence.filter((card) => card.revealed).length;
          const seatProgress = others.length <= 1 ? 0.5 : index / (others.length - 1);
          const arcSpan = [0, 0, 50, 68, 78, 80][Math.min(others.length, 5)] ?? 80;
          const arcDepth = [0, 0, 50, 75, 88, 94][Math.min(others.length, 5)] ?? 94;
          const normalizedSeatOffset = Math.abs(seatProgress - 0.5) * 2;
          const seatDepth = (1 - Math.cos((normalizedSeatOffset * Math.PI) / 2)) * arcDepth;
          const playerTurnIndex = alivePlayers.findIndex((alive) => alive.id === player.id);
          const turnsAway =
            activeTurnIndex >= 0 && playerTurnIndex >= 0
              ? (playerTurnIndex - activeTurnIndex + alivePlayers.length) % alivePlayers.length
              : null;
          const turnLabel = !player.alive
            ? "OUT"
            : turnsAway === 0
              ? "NOW"
              : turnsAway === 1
                ? "NEXT"
                : "";
          const turnDescription = !player.alive
            ? "Out of the game"
            : turnsAway === 0
              ? "Taking a turn now"
              : turnsAway === 1
                ? "Next in turn order"
                : turnsAway !== null
                  ? `${turnsAway} turns away`
                  : `Seat ${index + 1}`;
          const seatStyle = {
            "--seat-x": `${50 - arcSpan / 2 + seatProgress * arcSpan}%`,
            "--seat-y": `${22 + seatDepth}px`,
            "--seat-tilt": `${(seatProgress - 0.5) * 14}deg`,
            "--seat-layer": `${Math.round(8 - Math.abs(seatProgress - 0.5) * 4)}`
          } as React.CSSProperties;
          return (
            <motion.button
              type="button"
              layout
              key={player.id}
              style={seatStyle}
              // Hit-tested by the hold hook while a finger drags along the rail;
              // it is the only way to name the seat under an arbitrary point.
              data-seat-id={player.id}
              className={[
                "player-pill",
                player.bot ? "player-pill--bot" : "",
                player.id === activePlayerId ? "player-pill--active" : "",
                !player.alive ? "player-pill--out" : "",
                targeting && player.alive ? "player-pill--targetable" : ""
              ]
                .filter(Boolean)
                .join(" ")}
              // Says what this press will actually produce, never what the pill
              // wishes it produced. While a court panel is up the peek is the
              // compact one — lost cards only — and promising recent moves that
              // are not in the DOM is how a control becomes a lie.
              aria-label={`${player.name}${player.bot ? ", bot" : ""}. ${turnDescription}. ${lostCount} Influence lost. ${
                targeting && player.alive
                  ? "Tap to target, or hold to view their lost cards and recent moves"
                  : compact
                    ? "Tap to keep open, or hold to view their lost cards"
                    : "Tap to keep open, or hold to view their lost cards and recent moves"
              }.`}
              aria-current={player.id === activePlayerId ? "true" : undefined}
              {...hold.handlers(player.id)}
              onKeyDown={(event) => {
                // A held key auto-repeats, and every repeat used to restart the
                // 450ms timer, so holding Space could never reach the threshold
                // and the peek was unreachable without a pointer.
                if (event.repeat) return;
                if (event.key === " " || event.key === "Enter") hold.start(player.id);
              }}
              onKeyUp={(event) => {
                if (event.key === " " || event.key === "Enter") hold.end();
              }}
              onClick={hold.wrapClick(player.id, () => {
                // Targeting owns the tap — picking a rival is the whole point of
                // the mode, and the peek is still one hold away. Everywhere else
                // a tap pins the peek so a thumb can leave the screen to read it.
                if (targeting && player.alive) {
                  onTarget(player.id);
                  return;
                }
                hold.toggle(player.id);
              })}
            >
              {turnLabel && <span className="player-pill__order">{turnLabel}</span>}
              {player.alive && <CoinRing coins={player.coins} />}
              {/* The avatar circle, not the pill, is the coin/card anchor: the
                  seat arc is recomputed in JS every render, so the animation
                  layer measures this node instead of deriving a position. */}
              <span className="player-pill__avatar" ref={anchorRef(seatAnchor(player.id))}>
                {/* A face card wins over the initial; bots never carry one, so the
                    glyph below is still their only avatar. */}
                {player.avatar ? (
                  <img src={player.avatar} alt="" />
                ) : player.bot ? (
                  <Bot size={21} strokeWidth={2.3} />
                ) : (
                  player.name.slice(0, 1).toUpperCase()
                )}
              </span>
              <span className="player-pill__info">
                {/* The avatar already carries the bot glyph, so no BOT tag here. */}
                <span className="player-pill__name">
                  {player.name}
                  {!player.connected && <WifiOff size={11} />}
                </span>
                <span className="player-pill__meta">
                  {/* Below 7 the ring says everything and a numeral is noise.
                      From 7 the exact figure starts changing decisions — can
                      they Coup, can they still Assassinate after — so it comes
                      back, loudly. */}
                  {player.alive && player.coins >= COUP_COST && (
                    <motion.b
                      className={
                        player.coins >= COUP_FORCED
                          ? "coin-count coin-count--forced"
                          : "coin-count"
                      }
                      key={player.coins}
                      initial={{ scale: 1.5 }}
                      animate={{ scale: 1 }}
                      title={
                        player.coins >= COUP_FORCED
                          ? "Must Coup on their turn"
                          : "Can afford a Coup"
                      }
                    >
                      {player.coins}
                    </motion.b>
                  )}
                  <span className="influence-pips">
                    {player.influence.map((card) => (
                      <span
                        key={card.id}
                        className={card.revealed ? "influence-pip influence-pip--lost" : "influence-pip"}
                        title={card.revealed && card.role ? ROLES[card.role].name : "Hidden Influence"}
                        style={
                          card.revealed && card.role
                            ? ({ "--pip-color": ROLES[card.role].color } as React.CSSProperties)
                            : undefined
                        }
                      />
                    ))}
                  </span>
                </span>
              </span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {pinned && (
          // Only a pinned peek gets a backdrop: it is what a tap outside lands
          // on. A held peek needs none — the release already closes it, and a
          // catcher over the table would swallow the drag to the next seat.
          <motion.div
            key="seat-peek-backdrop"
            // Modified in step with the panel so the stylesheet can show the
            // compact peek and still keep the catcher hidden. Dismissal does not
            // depend on it: the X and Escape both work, and the compact peek
            // drops the footer that used to name the table as the way out.
            className={compact ? "seat-peek-backdrop seat-peek-backdrop--compact" : "seat-peek-backdrop"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
            aria-hidden="true"
            onClick={hold.unpin}
          />
        )}
        {peekPlayer && (
          <motion.aside
            key="seat-peek"
            // `--compact` rides alongside `--pinned`, never instead of it: a
            // compact peek is still pinnable and still needs pointer events for
            // its close button. It is the one variant that may be shown while a
            // court panel owns the middle of the table, so it carries the header
            // and the lost-card tiles and nothing else — checking what a rival
            // has already lost matters most in the seconds before you doubt,
            // which is precisely when the full panel is hidden.
            className={[
              "lost-card-peek",
              pinned ? "lost-card-peek--pinned" : "",
              compact ? "lost-card-peek--compact" : ""
            ]
              .filter(Boolean)
              .join(" ")}
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.98 }}
            transition={{ duration: 0.14 }}
          >
            <header>
              <span>
                <EyeOff size={17} />
                <b>{peekPlayer.name}</b>
              </span>
              <small>REVEALED</small>
              {/* Rendered only when pinned: while held the panel takes no
                  pointer events, so a close button would be a lie. */}
              {pinned && (
                <button
                  type="button"
                  className="lost-card-peek__close"
                  aria-label="Close"
                  onClick={hold.unpin}
                >
                  <X size={15} strokeWidth={3} />
                </button>
              )}
            </header>
            <div className="lost-card-peek__cards">
              {peekLostCards.length ? (
                peekLostCards.map((card) => {
                  if (!card.role) return null;
                  const role = ROLES[card.role];
                  return (
                    <span
                      className="lost-card-mini"
                      key={card.id}
                      style={{ "--role-color": role.color } as React.CSSProperties}
                    >
                      <img src={role.image} alt="" />
                      <span>
                        <b>{role.name}</b>
                        <small>LOST</small>
                      </span>
                    </span>
                  );
                })
              ) : (
                <span className="lost-card-peek__empty">No lost cards yet</span>
              )}
            </div>
            {/* The two rows the compact variant sheds, and the reason it can
                fit beside a court panel at all: recent moves are ~150px of the
                panel's ~275px. They are the part a player can come back for
                once the window has closed; the lost cards are the part that
                decides whether to doubt in the next five seconds. */}
            {!compact && (
              <section className="peek-plays">
                <header>
                  <b>Recent moves</b>
                  <small>Newest first</small>
                </header>
                <div className="peek-plays__list">
                  {peekPlays.length ? (
                    peekPlays.map((entry) => {
                      const action = ACTIONS[entry.actionType];
                      const role = action.role ? ROLES[action.role] : null;
                      const targetName = entry.targetId
                        ? players.find((player) => player.id === entry.targetId)?.name
                        : null;
                      return (
                        <div className="peek-plays__row" key={entry.id}>
                          <span
                            className={
                              role ? "peek-plays__art" : "peek-plays__art peek-plays__art--plain"
                            }
                            style={
                              role
                                ? ({ "--role-color": role.color } as React.CSSProperties)
                                : undefined
                            }
                          >
                            {role ? <img src={role.image} alt="" /> : null}
                          </span>
                          <b>{action.name}</b>
                          {targetName && <small>on {targetName}</small>}
                        </div>
                      );
                    })
                  ) : (
                    <span className="peek-plays__empty">Nothing declared yet</span>
                  )}
                </div>
              </section>
            )}
            {/* "Outside" stopped being true: the backdrop is scoped to empty
                table space now, so the header and the dock keep their own taps
                rather than being swallowed by a dismiss layer. Naming the table
                teaches the affordance that actually exists — the X and Escape
                both still work regardless.
                Dropped entirely while compact: the backdrop is the stylesheet's
                to show or hide there, so this line could not promise anything
                truthfully, and it is the cheapest row left to spend. */}
            {!compact && (
              <footer>{pinned ? "Tap the table to close" : "Release to close"}</footer>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
